import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { HiOutlineSquares2X2, HiOutlinePhoto, HiOutlineMagnifyingGlass } from 'react-icons/hi2';
import { useAdmin } from '../context/AdminContext';
import { PositionControls } from './PositionControls';

export function SeriesList({ series = [], paintings = [], onEdit, onDelete, entityViews = {} }) {
  const { t } = useTranslation();
  const { handleSeriesPositionUpdate, loading } = useAdmin();
  const [search, setSearch] = useState('');
  const [filteredSeries, setFilteredSeries] = useState([]);
  const [sortMode, setSortMode] = useState(false);
  const [positionInputs, setPositionInputs] = useState({});
  const [isUpdating, setIsUpdating] = useState(false);

  // Filter series based on search
  useEffect(() => {
    if (!Array.isArray(series)) {
      setFilteredSeries([]);
      return;
    }

    if (!search.trim()) {
      setFilteredSeries(series);
    } else {
      const searchLower = search.toLowerCase();
      const filtered = series.filter(s => 
        (s.name && s.name.toLowerCase().includes(searchLower))
      );
      setFilteredSeries(filtered);
    }
  }, [series, search]);

  // Position Update Handler
  const handlePositionUpdateWrapper = async (seriesId, newPosition) => {
    if (isUpdating) return;
    
    setIsUpdating(true);
    
    try {
      await handleSeriesPositionUpdate(seriesId, newPosition);
    } catch (error) {
      alert(t('serieslist.position_error', 'Fehler beim Aktualisieren der Position'));
    } finally {
      setIsUpdating(false);
    }
  };

  // Helper function to count paintings in a series
  const getPaintingCount = (seriesId) => {
    return paintings.filter(p => p.serie_id === seriesId).length;
  };

  return (
    <div>
      <h3 className="text-lg font-bold mb-4 text-gray-900 dark:text-white">{t('seriesform.current_series')}</h3>
      
      {/* Header mit Suchleiste und Sortier-Toggle */}
      <div className="flex flex-col md:flex-row gap-4 mb-6">
        {/* Search Bar */}
        <div className="flex-1 flex items-center gap-2 bg-white dark:bg-zinc-800 rounded-lg shadow px-3 py-2 border border-gray-200 dark:border-zinc-700">
          <HiOutlineMagnifyingGlass className="w-5 h-5 text-gray-400 dark:text-gray-500" />
          <input
            type="text"
            className="flex-1 bg-transparent outline-none text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 text-sm"
            placeholder={t('seriesform.search_placeholder', 'Suche in Serien...')}
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        {/* Sortier-Modus Toggle */}
        <button
          onClick={() => setSortMode(!sortMode)}
          disabled={isUpdating}
          className={`px-4 py-2 rounded-lg font-medium text-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
            sortMode 
              ? 'bg-violet-600 text-white shadow-lg' 
              : 'bg-white dark:bg-zinc-800 text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-zinc-700 shadow'
          }`}
        >
          {isUpdating && sortMode && <span className="mr-2">⟲</span>}
          {sortMode ? t('serieslist.sort_mode_on', 'Sortier-Modus aktiv') : t('serieslist.sort_mode_off', 'Sortier-Modus')}
        </button>
      </div>

      {loading ? (
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-gray-900 dark:border-gray-100 mx-auto" />
      ) : (!filteredSeries || filteredSeries.length === 0) ? (
        <div className="text-gray-500 text-center">{search ? t('seriesform.no_search_results', 'Keine Serien gefunden') : t('seriesform.no_series', 'Keine Serien vorhanden')}</div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 md:gap-4">
          {filteredSeries.map(s => {
            const paintingCount = getPaintingCount(s.id);
            return (
              <div key={s.id} className="flex flex-col h-full">
                {/* Main Card */}
                <div className="bg-white dark:bg-zinc-800 rounded-lg shadow hover:shadow-lg transition-shadow duration-200 border border-gray-200 dark:border-zinc-700 hover:border-violet-200 dark:hover:border-violet-700 flex flex-col h-full relative">
                  {/* Position Badge (only in sort mode) */}
                  {sortMode && (
                    <div className="absolute top-2 right-2 bg-violet-600 text-white text-xs font-bold px-2 py-1 rounded-full z-10">
                      #{s.sort_order}
                    </div>
                  )}
                  
                  {/* Content Section */}
                  <div className="p-2 md:p-3 flex flex-col gap-2 flex-grow">
                    {/* Title */}
                    <div className="flex gap-2">
                      <HiOutlineSquares2X2 className="text-violet-500 flex-shrink-0 mt-0.5" />
                      <div className="min-h-[2.5rem]">
                        <h3 className="font-semibold text-sm md:text-base text-gray-900 dark:text-white leading-tight" title={s.name}>
                          {s.name}
                        </h3>
                      </div>
                    </div>
                    
                    {/* Meta Info */}
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-1 text-xs text-gray-600 dark:text-gray-400">
                        <HiOutlinePhoto className="w-3 h-3" />
                        <span>{paintingCount}</span>
                      </div>
                      {typeof entityViews[s.id] === 'number' && (
                        <div className="flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400">
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="w-3 h-3"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 616 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.477 0 8.268 2.943 9.542 7-1.274 4.057-5.065 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                          {entityViews[s.id]}
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex gap-1 md:gap-2 mt-auto pt-1">
                      <button
                        type="button"
                        onClick={() => onEdit && onEdit(s)}
                        className="flex-1 px-2 md:px-3 py-1 md:py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded text-xs md:text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => onDelete && onDelete(s.id)}
                        className="flex-1 px-2 md:px-3 py-1 md:py-1.5 bg-red-600 hover:bg-red-700 text-white rounded text-xs md:text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-red-500"
                      >
                        Del
                      </button>
                    </div>
                  </div>
                </div>
                
                {/* Position Controls */}
                <PositionControls
                  item={s}
                  totalItems={series.length}
                  positionInputs={positionInputs}
                  setPositionInputs={setPositionInputs}
                  onPositionUpdate={handlePositionUpdateWrapper}
                  isUpdating={isUpdating}
                  sortMode={sortMode}
                />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}